import React, { useMemo } from 'react';

// ─── Tiny inline sparkline (pure CSS bar chart) ─────────────────────────────
const MiniBarChart = ({ data, color = 'var(--pico-primary)', height = 36 }) => {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data, 1);
  return (
    <div className="mini-bar-chart" style={{ height }}>
      {data.map((v, i) => (
        <div
          key={i}
          className="mini-bar"
          style={{
            height: `${(v / max) * 100}%`,
            background: color,
          }}
          title={String(v)}
        />
      ))}
    </div>
  );
};

// ─── Progress ring (SVG) ────────────────────────────────────────────────────
const ProgressRing = ({ value, max, size = 56, stroke = 5, color = 'var(--pico-primary)' }) => {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  return (
    <svg width={size} height={size} className="progress-ring">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke="var(--pico-muted-border-color)" strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={color} strokeWidth={stroke}
        strokeDasharray={`${circ * pct} ${circ * (1 - pct)}`}
        strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em"
        className="progress-ring-text">
        {Math.round(pct * 100)}%
      </text>
    </svg>
  );
};

// ─── Helpers ────────────────────────────────────────────────────────────────
const bucketByDay = (items, dayCount, getTimestamp) => {
  const now = Date.now();
  const buckets = Array(dayCount).fill(0);
  items.forEach(item => {
    const ts = getTimestamp(item);
    if (!ts) return;
    const daysAgo = Math.floor((now - ts) / 86400000);
    if (daysAgo >= 0 && daysAgo < dayCount) buckets[dayCount - 1 - daysAgo]++;
  });
  return buckets;
};

const AdminDashboard = ({
  users,
  loading,
  organizations,
  projectsByOrg,
  totalProjects,
  totalMessages,
  documents,
  serverHealth,
  activityLogs,
  shapeCountsByOrg,
  t,
}) => {
  const totalUsers = users.length;
  const documentCount = documents.length;
  const DAY = 86400000;
  const now = Date.now();

  // User growth
  const recentUsers7d = useMemo(() =>
    users.filter(u => u.createdAt && (now - new Date(u.createdAt).getTime()) < 7 * DAY).length,
    [users, now]);

  const userGrowthSpark = useMemo(() =>
    bucketByDay(users, 14, u => u.createdAt ? new Date(u.createdAt).getTime() : null),
    [users]);

  // Activity sparkline (last 14 days)
  const activitySpark = useMemo(() =>
    bucketByDay(activityLogs, 14, l => l.timestamp),
    [activityLogs]);

  // Users per role breakdown
  const roleBreakdown = useMemo(() => {
    const counts = { ADMIN: 0, USER: 0, GUEST: 0 };
    users.forEach(u => {
      if (u.roles) {
        Object.values(u.roles).forEach(role => {
          if (role && counts[role] !== undefined) counts[role]++;
        });
      }
    });
    return counts;
  }, [users]);

  // Users per org
  const usersByOrg = useMemo(() => {
    const map = {};
    users.forEach(u => {
      if (u.roles) {
        Object.keys(u.roles).forEach(org => {
          if (u.roles[org] !== null) map[org] = (map[org] || 0) + 1;
        });
      }
    });
    return map;
  }, [users]);

  // Verified vs unverified
  const verifiedCount = useMemo(() =>
    users.filter(u => u.emailVerified).length,
    [users]);

  // Total annotated pages across orgs
  const totalAnnotatedPages = useMemo(() =>
    Object.values(shapeCountsByOrg).reduce((sum, c) => sum + (c.pages || 0), 0),
    [shapeCountsByOrg]);

  // Provider breakdown (reads signInProvider synced to RTDB on each login)
  const providerStats = useMemo(() => {
    const counts = { password: 0, google: 0, microsoft: 0 };
    users.forEach(u => {
      const provider = u.signInProvider || 'password';
      if (provider === 'google') counts.google++;
      else if (provider === 'microsoft') counts.microsoft++;
      else counts.password++;
    });
    return counts;
  }, [users]);

  const isHealthy = serverHealth?.healthy;

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="shimmer-grid">
          {Array(6).fill(null).map((_, i) => (
            <div key={i} className="shimmer-card">
              <div className="placeholder-line" />
              <div className="placeholder-line short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-overview">
      {/* ── Row 1: Primary KPIs ────────────────────────────────────────── */}
      <div className="kpi-grid primary">
        <div className="kpi-card accent-blue">
          <div className="kpi-label"><span>👥</span> {t('admin.stats.total_users')}</div>
          <div className="kpi-value">{totalUsers}</div>
          <MiniBarChart data={userGrowthSpark} color="rgba(59, 130, 246, 0.4)" />
          <div className="kpi-subtext">
            <span className="kpi-delta positive">+{recentUsers7d}</span>
            <span>{t('admin.stats.last_7d')}</span>
          </div>
        </div>

        <div className="kpi-card accent-violet">
          <div className="kpi-label"><span>🏢</span> {t('admin.stats.active_roles')}</div>
          <div className="kpi-value">{organizations.length}</div>
          <div className="kpi-subtext">
            <span>{totalProjects} {t('admin.stats.total_projects').toLowerCase()}</span>
          </div>
        </div>

        <div className="kpi-card accent-amber">
          <div className="kpi-label"><span>📄</span> {t('admin.stats.total_documents')}</div>
          <div className="kpi-value">{documentCount}</div>
          <div className="kpi-subtext">
            <span>{totalAnnotatedPages} {t('admin.stats.annotated_pages')}</span>
          </div>
        </div>

        <div className="kpi-card accent-green">
          <div className="kpi-label"><span>⚡</span> {t('admin.stats.system_status')}</div>
          <div className="kpi-value kpi-status-value">
            <span className={`system-status-indicator ${isHealthy ? '' : 'status-degraded'}`} />
            {isHealthy ? t('admin.stats.status_online') : 'Down'}
          </div>
          <div className="kpi-subtext">
            {serverHealth?.responseTime != null && <span>{serverHealth.responseTime}ms</span>}
            {serverHealth?.version && <span className="version-badge">v{serverHealth.version}</span>}
          </div>
        </div>
      </div>

      {/* ── Row 2: Server Health + Activity sparkline ──────────────────── */}
      <div className="metrics-row two-col">
        <section className="metric-card glass">
          <h5>{t('admin.health.server_vitals')}</h5>
          <div className="vitals-grid">
            <div className="vital-item">
              <span className="vital-label">{t('admin.health.database')}</span>
              <span className={`vital-status ${serverHealth?.database === 'healthy' ? 'ok' : 'warn'}`}>
                {serverHealth?.database || '—'}
              </span>
            </div>
            <div className="vital-item">
              <span className="vital-label">{t('admin.health.disk_space')}</span>
              <span className="vital-status">
                {serverHealth?.diskSpaceGb != null ? `${serverHealth.diskSpaceGb} GB` : '—'}
              </span>
            </div>
            <div className="vital-item">
              <span className="vital-label">{t('admin.health.disk_status')}</span>
              <span className={`vital-status ${serverHealth?.diskStatus === 'healthy' ? 'ok' : 'warn'}`}>
                {serverHealth?.diskStatus || '—'}
              </span>
            </div>
            <div className="vital-item">
              <span className="vital-label">{t('admin.health.latency')}</span>
              <span className="vital-status">
                {serverHealth?.responseTime != null ? `${serverHealth.responseTime}ms` : '—'}
              </span>
            </div>
            {serverHealth?.details?.email_service && (
              <div className="vital-item">
                <span className="vital-label">Email</span>
                <span className={`vital-status ${serverHealth.details.email_service.status === 'ok' ? 'ok' : 'warn'}`}>
                  {serverHealth.details.email_service.status || '—'}
                </span>
              </div>
            )}
          </div>
        </section>

        <section className="metric-card glass">
          <h5>{t('admin.stats.activity_14d')}</h5>
          <MiniBarChart data={activitySpark} color="rgba(139, 92, 246, 0.4)" height={64} />
          <div className="heatmap-legend">
            <span>{t('admin.stats.total_events')}: {activityLogs.length}</span>
          </div>
        </section>
      </div>

      {/* ── Row 3: User Analytics ──────────────────────────────────────── */}
      <div className="metrics-row three-col">
        <section className="metric-card glass">
          <h5>{t('admin.stats.role_distribution')}</h5>
          <div className="distribution-bars">
            {Object.entries(roleBreakdown).map(([role, count]) => (
              <div key={role} className="dist-row">
                <span className="dist-label">{role}</span>
                <div className="dist-bar-track">
                  <div className="dist-bar-fill"
                    style={{ width: `${totalUsers > 0 ? (count / totalUsers) * 100 : 0}%` }} />
                </div>
                <span className="dist-count">{count}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="metric-card glass center-content">
          <h5>{t('admin.stats.email_verified')}</h5>
          <ProgressRing value={verifiedCount} max={totalUsers} color="#10b981" />
          <div className="ring-legend">
            <span>{verifiedCount} / {totalUsers}</span>
          </div>
        </section>

        <section className="metric-card glass">
          <h5>{t('admin.stats.auth_providers')}</h5>
          <div className="distribution-bars">
            {Object.entries(providerStats).filter(([, c]) => c > 0).map(([provider, count]) => (
              <div key={provider} className="dist-row">
                <span className="dist-label">{provider}</span>
                <div className="dist-bar-track">
                  <div className="dist-bar-fill provider"
                    style={{ width: `${totalUsers > 0 ? (count / totalUsers) * 100 : 0}%` }} />
                </div>
                <span className="dist-count">{count}</span>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* ── Row 4: Org breakdown table ─────────────────────────────────── */}
      <section className="metric-card glass full-width">
        <h5>{t('admin.stats.org_overview')}</h5>
        <div className="org-table-container">
          <table className="org-table">
            <thead>
              <tr>
                <th>{t('vcontrol.organisation')}</th>
                <th>{t('admin.stats.total_users')}</th>
                <th>{t('admin.stats.total_projects')}</th>
                <th>{t('admin.stats.annotated_pages')}</th>
              </tr>
            </thead>
            <tbody>
              {organizations.map(org => {
                const orgProjects = projectsByOrg[org] || [];
                const orgUsers = usersByOrg[org] || 0;
                const orgShapes = shapeCountsByOrg[org] || {};
                return (
                  <tr key={org}>
                    <td><span className="org-name-cell">{org}</span></td>
                    <td>{orgUsers}</td>
                    <td>{orgProjects.length}</td>
                    <td>{orgShapes.pages || 0}</td>
                  </tr>
                );
              })}
              {organizations.length === 0 && (
                <tr><td colSpan={4} className="empty-row">No organizations found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Row 5: Recent Activity Feed ────────────────────────────────── */}
      <section className="metric-card glass full-width">
        <h5>{t('admin.tab_activity')} — {t('admin.stats.last_7d')}</h5>
        <div className="mini-activity-feed">
          {activityLogs.slice(0, 8).map(log => (
            <div key={log.id} className="mini-log-entry">
              <span className="mini-log-dot" />
              <span className="mini-log-action">{log.action.replace(/_/g, ' ')}</span>
              <span className="mini-log-org">{log.organization}</span>
              <span className="mini-log-actor">{log.actorEmail || '—'}</span>
              <span className="mini-log-time">
                {log.timestamp ? new Date(log.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
              </span>
            </div>
          ))}
          {activityLogs.length === 0 && (
            <p className="empty-row">No recent activity</p>
          )}
        </div>
      </section>
    </div>
  );
};

export default AdminDashboard;
